import { useEffect, useMemo, useState } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Check, ChevronLeft, LoaderCircle, Users, Zap } from 'lucide-react';
import * as yup from 'yup';

import { fetchPaddleCheckoutBootstrap } from '@/api/billing/fetchPaddleCheckoutBootstrap';
import { usePaddleCheckoutPlans } from '@/api/billing/usePaddleCheckoutPlans';
import { writePendingCompanyName, writePendingTenantId, clearPendingCompanyName } from '@/api/tenants/linkClerkOrg';
import { useRegisterUser } from '@/api/users/useRegisterUser';
import AuthLayout from '@/component-library/layouts/AuthLayout/AuthLayout';
import { Button } from '@/component-library/primitives/Button/Button';
import CircleWarningIcon from '@/component-library/primitives/Icons/CircleWarningIcon/CircleWarningIcon';
import Link from '@/component-library/primitives/Link/Link';
import { Input } from '@/component-library/widgets/Input/Input';
import { openPaddleSubscriptionCheckout } from '@/lib/paddle/openPaddleSubscriptionCheckout';
import type { PaddleCheckoutPlan } from '@/models/PaddleCheckoutPlan';
import { RegistrationData } from '@/models/RegistrationData';
import { isClerkAuthEnabled } from '@/utils/auth/authMode';
import { isPathBCustomOrgConfirmEnabled } from '@/utils/auth/pathBOrgConfirm';
import { AppRoutes } from '@/utils/routes/router';

interface SignUpFormValues {
  firstName: string;
  lastName: string;
  userEmail: string;
  tenantName: string;
  tenantAddress: string;
}

function formatPaddleLimit(n: number): string {
  if (n === -1) return 'Unlimited';
  return n.toLocaleString();
}

const PaddleSignUpPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') ?? '';
  /** Collect company name only when custom org-confirm will reuse it (not Clerk default). */
  const collectCompanyName = isPathBCustomOrgConfirmEnabled();

  const { data: paddlePlans = [], isLoading: paddlePlansLoading } = usePaddleCheckoutPlans();
  /** Signup billing is Paddle-only; no Stripe fallback. */
  const checkoutMode = paddlePlansLoading ? 'loading' : paddlePlans.length > 0 ? 'paddle' : 'noPlans';

  const schema = useMemo(
    () =>
      yup
        .object({
          userEmail: yup
            .string()
            .email(t('CommonErrors.emailFormatValidation'))
            .required(t('CommonErrors.emailRequired')),
          tenantName: collectCompanyName
            ? yup.string().trim().required(t('CommonErrors.companyNameRequired'))
            : yup.string().trim().optional().default(''),
          tenantAddress: yup.string().required(t('CommonErrors.tenantAddressRequired')),
          firstName: yup.string().required(t('CommonErrors.firstNameRequired')),
          lastName: yup.string().required(t('CommonErrors.lastNameRequired')),
        })
        .required(),
    [t, collectCompanyName],
  );

  const { register, handleSubmit, setValue, getValues, trigger, formState: { errors } } = useForm<SignUpFormValues>({
    resolver: yupResolver(schema as yup.ObjectSchema<SignUpFormValues>) as Resolver<SignUpFormValues>,
  });

  useEffect(() => {
    if (email) {
      setValue('userEmail', email);
    }
  }, [email, setValue]);

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [paddleTenantId, setPaddleTenantId] = useState<string | null>(null);

  const registerUserMutation = useRegisterUser();
  const isLoading = registerUserMutation.isLoading;
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [payBusy, setPayBusy] = useState(false);

  const selectedPaddlePlan = useMemo(
    () => (selectedPlanId ? paddlePlans.find((p) => p.paddlePriceId === selectedPlanId) : null),
    [paddlePlans, selectedPlanId],
  );

  const onStep1Next = (_data: SignUpFormValues) => {
    setErrorMessage('');
    setStep(2);
  };

  const buildRegistrationBase = (data: SignUpFormValues, tenantTier: string): RegistrationData => ({
    ...data,
    tenantTier,
    inquiryId: searchParams.get('id') ?? '',
    messagingGoal: 'retention',
    dataCenter: 'eu-central-1',
    userPhone: '+1234567890',
    // Clerk default: name comes from Clerk org setup later; API falls back to email local-part.
    tenantName: collectCompanyName ? data.tenantName.trim() : '',
    tenantPhone: '+1234567890',
  });

  const setRegisterError = (error: unknown) => {
    console.error('Registration error:', error);
    const err = error as { response?: { status?: number; data?: unknown } };
    const status = err?.response?.status;
    const serverMsg = err?.response?.data;
    if (status === 409) {
      setErrorMessage(
        t('SignUpPage.userExistsError') ||
          'User with this email already exists. Please use a different email or sign in.',
      );
    } else if (status === 400) {
      setErrorMessage(
        typeof serverMsg === 'string' && serverMsg ? serverMsg : 'Invalid data provided. Please check your input.',
      );
    } else if (typeof serverMsg === 'string' && serverMsg) {
      setErrorMessage(serverMsg);
    } else if (error && typeof error === 'object' && 'message' in error && typeof (error as Error).message === 'string') {
      setErrorMessage((error as Error).message);
    } else {
      setErrorMessage(t('SignUpPage.errorOccurred') || 'An error occurred during registration. Please try again.');
    }
  };

  const onPaddleContinueToPayment = async () => {
    setErrorMessage('');
    if (!selectedPlanId || checkoutMode !== 'paddle') {
      setErrorMessage(t('SignUpPage.selectPlanRequired') || 'Please select a plan.');
      return;
    }
    const data = getValues();
    const valid = await trigger();
    if (!valid) {
      setErrorMessage(t('SignUpPage.checkFields') || 'Please check the form fields.');
      return;
    }
    try {
      const registrationData: RegistrationData = {
        ...buildRegistrationBase(data, selectedPaddlePlan ? 'PADDLE' : 'BASIC'),
        paddlePriceId: selectedPlanId,
      };
      const body = await registerUserMutation.mutateAsync(registrationData);
      const tid = body.tenantId;
      if (!tid) {
        setErrorMessage(
          t('SignUpPage.paddleMissingTenantId') ||
            'Registration did not return a tenant id. Configure your /registration API to return tenantId before payment.',
        );
        return;
      }
      setPaddleTenantId(tid);
      writePendingTenantId(tid);
      if (collectCompanyName) {
        const companyName = (body.tenantName || data.tenantName).trim();
        writePendingCompanyName(companyName);
        console.info('[ChurnSignal][PathB-Org] registration stored pending Path B keys', {
          tenantId: tid,
          companyName,
        });
      } else {
        // Drop stale company name from older custom-mode / email-local-part runs.
        clearPendingCompanyName();
        console.info('[ChurnSignal][PathB-Org] registration stored pending tenant (Clerk org name later)', {
          tenantId: tid,
        });
      }
      setStep(3);
    } catch (error: unknown) {
      setRegisterError(error);
    }
  };

  const onPaddlePayNow = async () => {
    setErrorMessage('');
    const data = getValues();
    if (!paddleTenantId) {
      setErrorMessage(t('SignUpPage.paddleMissingTenantId') || 'Missing tenant id.');
      return;
    }
    // Re-assert Path B storage before checkout / Clerk sign-up (new tab / refresh safe).
    writePendingTenantId(paddleTenantId);
    if (collectCompanyName) {
      writePendingCompanyName(data.tenantName.trim());
    }
    setPayBusy(true);
    try {
      const { token, sandbox } = await fetchPaddleCheckoutBootstrap();
      const successUrl = isClerkAuthEnabled()
        ? `${window.location.origin}${AppRoutes.signUp}`
        : `${window.location.origin}${AppRoutes.verifyEmail}?email=${encodeURIComponent(data.userEmail)}`;
      await openPaddleSubscriptionCheckout({
        clientToken: token,
        sandbox,
        priceId: selectedPlanId!,
        customerEmail: data.userEmail,
        tenantId: paddleTenantId,
        successUrl,
      });
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      const msg = err?.response?.data?.error;
      setErrorMessage(typeof msg === 'string' ? msg : t('SignUpPage.paddleCheckoutError') || 'Checkout failed.');
    } finally {
      setPayBusy(false);
    }
  };

  const totalSteps = checkoutMode === 'paddle' ? 3 : 2;
  const stepCircle = (n: 1 | 2 | 3) =>
    `flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium ${
      step === n ? 'bg-custom-blue text-white' : step > n ? 'bg-custom-blue/20 text-custom-blue' : 'bg-gray-200 text-custom-darkSilver'
    }`;

  const renderStepIndicator = () => (
    <div className="mb-6 flex items-center justify-center gap-2">
      <span className={stepCircle(1)}>1</span>
      <div className="h-0.5 w-8 bg-gray-200" />
      <span className={stepCircle(2)}>2</span>
      {totalSteps === 3 && (
        <>
          <div className="h-0.5 w-8 bg-gray-200" />
          <span className={stepCircle(3)}>3</span>
        </>
      )}
    </div>
  );

  const renderPaddlePlanCard = (plan: PaddleCheckoutPlan) => {
    const selected = selectedPlanId === plan.paddlePriceId;
    return (
      <button
        key={plan.id}
        type="button"
        onClick={() => setSelectedPlanId(plan.paddlePriceId)}
        className={`relative flex w-full min-w-0 flex-col rounded-xl border-2 p-5 text-left shadow-sm transition-all hover:shadow-md ${
          selected
            ? 'border-custom-blue bg-custom-blue/5 ring-2 ring-custom-blue/30'
            : 'border-gray-200 bg-white hover:border-gray-300'
        }`}
        data-cy={`plan-${plan.paddlePriceId}`}
      >
        {selected && (
          <span className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full bg-custom-blue">
            <Check className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />
          </span>
        )}
        <span className="pr-8 text-lg font-semibold leading-snug text-custom-black">{plan.name}</span>
        {plan.description && (
          <span className="mt-2 text-sm leading-relaxed text-custom-darkSilver">{plan.description}</span>
        )}
        <ul className="mt-4 space-y-2.5">
          <li className="flex items-center gap-2 text-sm text-custom-black">
            <Users className="h-4 w-4 shrink-0 text-custom-blue" />
            <span>
              {t('SignUpPage.planUsers') || 'Users'}: {formatPaddleLimit(plan.numberOfUsers)}
            </span>
          </li>
          <li className="flex items-center gap-2 text-sm text-custom-black">
            <Zap className="h-4 w-4 shrink-0 text-custom-blue" />
            <span>
              {t('SignUpPage.planEvents') || 'Events'}: {formatPaddleLimit(plan.maxEventsPerMonth)}
            </span>
          </li>
        </ul>
      </button>
    );
  };

  const planGridClassName =
    paddlePlans.length <= 1
      ? 'grid grid-cols-1'
      : paddlePlans.length === 2
        ? 'grid grid-cols-1 gap-4 sm:grid-cols-2'
        : 'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3';

  return (
    <AuthLayout modalWidth="large">
      {errorMessage && (
        <div className="mb-6 p-3 bg-custom-red/10 rounded-lg border border-custom-red/30">
          <p className="text-custom-red text-sm font-medium flex items-start gap-2">
            <CircleWarningIcon className="shrink-0 mt-0.5" />
            <span>{errorMessage}</span>
          </p>
        </div>
      )}

      {renderStepIndicator()}

      {step !== 3 && (
        <form
          onSubmit={handleSubmit((data) => {
            if (step === 1) {
              onStep1Next(data);
            }
          })}
          className="flex flex-col w-full space-y-6"
        >
          {step === 1 && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  placeholder={t('SignUpPage.firstNamePlaceholder')}
                  label={t('SignUpPage.firstNameLabel')}
                  {...register('firstName')}
                  error={errors.firstName?.message}
                  className="w-full"
                  data-cy="firstName"
                />
                <Input
                  placeholder={t('SignUpPage.lastNamePlaceholder')}
                  label={t('SignUpPage.lastNameLabel')}
                  {...register('lastName')}
                  error={errors.lastName?.message}
                  className="w-full"
                  data-cy="lastName"
                />
              </div>
              <Input
                placeholder={t('SignUpPage.userEmailPlaceholder')}
                label={t('SignUpPage.userEmailLabel')}
                {...register('userEmail', { required: 'Email is required!' })}
                error={errors.userEmail?.message}
                className="w-full"
                data-cy="userEmail"
              />
              {collectCompanyName && (
                <Input
                  placeholder={t('SignUpPage.tenantNamePlaceholder')}
                  label={t('SignUpPage.tenantNameLabel')}
                  {...register('tenantName')}
                  error={errors.tenantName?.message}
                  className="w-full"
                  data-cy="tenantName"
                />
              )}
              <Input
                placeholder={t('SignUpPage.tenantAddressPlaceholder')}
                label={t('SignUpPage.tenantAddressLabel')}
                {...register('tenantAddress')}
                error={errors.tenantAddress?.message}
                className="w-full"
                data-cy="tenantAddress"
              />
              <Button
                type="submit"
                className="w-full bg-custom-blue hover:bg-custom-darkBlue text-white font-semibold py-3 rounded-lg transition-colors"
                data-cy="buttonSignUpNext"
              >
                {t('SignUpPage.buttonNext') || 'Next'}
              </Button>
            </>
          )}

          {step === 2 && (
            <>
              <div className="space-y-3">
                <label className="block text-sm font-medium text-custom-black">
                  {t('SignUpPage.planLabel') || 'Choose a plan'}
                </label>
                {checkoutMode === 'loading' && (
                  <div className="flex items-center gap-2 text-sm text-custom-darkSilver">
                    <LoaderCircle className="h-5 w-5 animate-spin text-custom-blue" />
                    {t('SignUpPage.plansLoading')}
                  </div>
                )}
                {checkoutMode === 'paddle' && (
                  <div className={planGridClassName} data-cy="planSelector">
                    {paddlePlans.map(renderPaddlePlanCard)}
                  </div>
                )}
                {checkoutMode === 'noPlans' && (
                  <div
                    className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950"
                    data-cy="paddlePlansUnavailable"
                  >
                    <p className="font-medium text-amber-950">{t('SignUpPage.paddlePlansUnavailableTitle')}</p>
                    <p className="mt-2 text-amber-900/90">{t('SignUpPage.paddlePlansUnavailableBody')}</p>
                  </div>
                )}
              </div>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="secondary"
                  className="flex-1"
                  onClick={() => setStep(1)}
                  data-cy="buttonSignUpBack"
                >
                  <ChevronLeft className="mr-1 h-4 w-4" />
                  {t('SignUpPage.buttonBack') || 'Back'}
                </Button>
                {checkoutMode === 'paddle' ? (
                  <Button
                    type="button"
                    className="flex-1 bg-custom-blue hover:bg-custom-darkBlue text-white font-semibold py-3 rounded-lg transition-colors"
                    data-cy="buttonPaddleContinue"
                    disabled={isLoading || !selectedPlanId}
                    onClick={() => void onPaddleContinueToPayment()}
                  >
                    {t('SignUpPage.buttonContinueToPayment')}
                    {isLoading && <LoaderCircle height={20} className="animate-spin" />}
                  </Button>
                ) : null}
              </div>
            </>
          )}
        </form>
      )}

      {step === 3 && checkoutMode === 'paddle' && (
        <div className="flex flex-col space-y-6">
          <p className="text-sm text-custom-darkSilver">{t('SignUpPage.paddleStep3Intro')}</p>
          {isClerkAuthEnabled() && (
            <p className="rounded-lg border border-custom-blue/20 bg-custom-blue/5 p-4 text-sm text-custom-darkSilver">
              Paddle payment can be completed later. Your company record is saved —{' '}
              <RouterLink to={AppRoutes.signUp} className="font-medium text-custom-blue hover:text-custom-darkBlue">
                create your account with the same email
              </RouterLink>{' '}
              to link your organization and open the dashboard.
            </p>
          )}
          <div className="flex gap-3">
            <Button
              type="button"
              variant="secondary"
              className="flex-1"
              onClick={() => {
                setStep(2);
                setPaddleTenantId(null);
              }}
            >
              <ChevronLeft className="mr-1 h-4 w-4" />
              {t('SignUpPage.buttonBack') || 'Back'}
            </Button>
            <Button
              type="button"
              className="flex-1 bg-custom-blue hover:bg-custom-darkBlue text-white font-semibold py-3 rounded-lg transition-colors"
              disabled={payBusy}
              onClick={() => void onPaddlePayNow()}
            >
              {t('SignUpPage.buttonPayNow')}
              {payBusy && <LoaderCircle height={20} className="ml-2 inline animate-spin" />}
            </Button>
          </div>
        </div>
      )}

      <div className="mt-6 text-center text-sm text-custom-darkSilver">
        {t('SignUpPage.linkAlreadyHaveAnAccount')}
        <Link
          className="ml-1 text-custom-blue hover:text-custom-darkBlue transition-colors no-underline"
          to={AppRoutes.signIn}
          label={t('SignUpPage.linkAlreadyHaveAnAccountLogIn')}
        />
      </div>
    </AuthLayout>
  );
};

export default PaddleSignUpPage;
